// src/components/SpikeHistogramChart.js
import { useMemo, useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';

export function SpikeHistogramChart({ spikeBuffer, drugTimestamp, bufferSize, experimentStartTime, neuronIds= [] }) {
    const binWidth = 10; // seconds
    const binCount = Math.ceil(bufferSize / binWidth);
    const roundedMax = binCount * binWidth;
    const spikeCountBuffer = useRef(new Map());
    const lastProcessedTime = useRef(0);
    const neuronCountBuffers = useRef(new Map());

    const drugTime = typeof drugTimestamp === 'string'
        ? new Date(drugTimestamp).getTime() / 1000
        : drugTimestamp;

        let drugX = null;

        if (drugTime != null && experimentStartTime != null) {
            drugX = drugTime - experimentStartTime;
        }

    useEffect(() => {
    // Track last processed timestamp per neuron
    if (!lastProcessedTime.current || !(lastProcessedTime.current instanceof Map)) {
        lastProcessedTime.current = new Map();
    }

    spikeBuffer.forEach(spike => {
        const id = spike.neuron_id || 'unknown';
        const neuronTimes = lastProcessedTime.current;

        const lastTimeForNeuron = neuronTimes.get(id) || 0;

        // Only skip if this spike is older than what we've already processed for THIS neuron
        if (typeof spike.relativeTime === 'number' && spike.timestamp > lastTimeForNeuron) {
        const bin = Math.floor(spike.relativeTime / binWidth);

        // Update population buffer
        spikeCountBuffer.current.set(
            bin,
            (spikeCountBuffer.current.get(bin) || 0) + 1
        );

        // Update per-neuron buffer
        if (!neuronCountBuffers.current.has(id)) {
            neuronCountBuffers.current.set(id, new Map());
        }
        const neuronBuffer = neuronCountBuffers.current.get(id);
        neuronBuffer.set(bin, (neuronBuffer.get(bin) || 0) + 1);

        // Update last processed time for THIS neuron only
        neuronTimes.set(id, spike.timestamp);
        }
    });
    }, [spikeBuffer]);


    const spikeCounts = useMemo(() => {
        const binCenters = Array.from({ length: binCount }, (_, i) => (i + 0.5) * binWidth);

        // Population histogram
        const populationCounts = new Map();

        const populationData = binCenters.map((x, i) => ({
            x,
            y: spikeCountBuffer.current.get(i) || 0,
        }));


        // Per-neuron histograms
        const spikesByNeuron = new Map();

/*
        const neuronHistograms = Array.from(neuronCountBuffers.current.entries()).map(([neuron_id, buffer]) => {
            const data = binCenters.map((x, i) => ({
                x,
                y: buffer.get(i) || 0,
            }));
            return { neuron_id, data };
        });
*/
        const allNeuronIds = Array.from(new Set([
            ...neuronIds,
            ...Array.from(neuronCountBuffers.current.keys())
        ]));

        const neuronHistograms = allNeuronIds.map(neuron_id => {
            const buffer = neuronCountBuffers.current.get(neuron_id) || new Map();
            const data = binCenters.map((x, i) => ({
                x,
                y: buffer.get(i) || 0,
            }));
            return { neuron_id, data };
        });


        console.log("Population spike histogram:", populationData);
        console.log("Neuron histograms:", neuronHistograms);

        neuronHistograms.sort((a, b) => {
            const idA = a.neuron_id.toLowerCase();
            const idB = b.neuron_id.toLowerCase();
            return idA.localeCompare(idB, undefined, { numeric: true });
        });

        return { populationData, neuronHistograms }
    }, [spikeBuffer, bufferSize]);


    const chartOptions = {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                type: 'linear',
                offset: false,
                grid: {
                    offset: false,
                },
                ticks: {
                    stepSize: binWidth,
                    callback: val => `${val}`,
                    maxTicksLimit: 10,
                },
                title: { display: true, text: 'Time (seconds)' },
            },
            y: {
                title: { display: true, text: 'Spike count' },
                beginAtZero: true,
                min: 0,
                suggestedMax: 50,
            },
        },
        plugins: {
            legend: {
            display: false,
            },
            tooltip: {
            callbacks: {
            title: items => {
                if (!items.length) return '';
                    const x = items[0].parsed.x;
                    const min = x - binWidth / 2;
                    const max = x + binWidth / 2;
                return `Time: ${min}s – ${max}s`;
                    },
                },
            },

            annotation: {
                annotations: drugX != null ? {
                    drugLine: {
                        type: 'line',
                        xMin: drugX,
                        xMax: drugX,
                        borderColor: 'blue',
                        borderWidth: 2,
                        label: {
                            content: 'Drug Applied',
                            enabled: true,
                            position: 'start',
                            backgroundColor: 'rgba(0,0,255,0.1)',
                            color: 'blue',
                            font: { weight: 'bold' },
                        },
                    }
                } : {},
            }

        },
    };

    function createHistogramOptions(yLabel) {
        return {
            ...chartOptions,
            scales: {
            ...chartOptions.scales,
            y: {
                ...chartOptions.scales.y,
                title: {
                display: true,
                text: yLabel,
                font: {
                    size: 11,
                    weight: 'bold',
                },
                },
            },
            },
        };
    }


    return(
        <>
            {/* Population histogram */}
            <div className="chart-section">
                {/* <h4>Population</h4> */}
                    <div className="chart-bar">
                        <Bar
                            data={{
                                datasets: [{
                                    label: 'Population Spike Count',
                                    data: spikeCounts.populationData,
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    barPercentage: 1.0,
                                    categoryPercentage: 1.0,
                                    borderRadius: 2,
                                }],
                            }}
                            options={createHistogramOptions('Pop. Spike Count')}
                        />
                    </div>
            </div>

            {/* Per-neuron histograms */}
            {spikeCounts.neuronHistograms.map(({ neuron_id, data }) => (
                <div key={neuron_id} className="chart-section" >  { /* style={{ flex: '1 1 300px', minWidth: '250px' }}>  */ }
                    {/* <h4>Neuron {neuron_id}</h4> */}
                    <div className="chart-bar">
                        <Bar
                            data={{
                                datasets: [{
                                    label: `Spikes for ${neuron_id}`,
                                    data,
                                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                    barPercentage: 1.0,
                                    categoryPercentage: 1.0,
                                    borderRadius: 2,
                                }],
                            }}
                            options={createHistogramOptions(`${neuron_id.toUpperCase()} Spike Count`)}
                        />
                    </div>
                </div>
            ))}
        </>
    );
}